'use client';

/* Hallmark · genre: editorial · macrostructure: Narrative Workflow — R8 · theme: custom (warm ledger) · design-system: R8 contract */

/**
 * HeroConvergenceFigure — R8 restructure (§H "fragmented -> CENTRALIZED ->
 * Cosmos/CMG"), `.claude/handoffs/2026-08-12-r8-restructure-contract.md`.
 * Replaces `HeroJourneyFigure` (deleted this change) as the hero's
 * right-column exhibit: the page's opening argument for the new
 * positioning — seven REAL shipped ingest paths, each with the real failure
 * mode Albert reports, converging through one amber "CENTRALIZED" beat into
 * the product itself.
 *
 * LIGHT-PAPER EXHIBIT, NOT a dark `FigureFrame` (contract §H: "this one is
 * a light-paper exhibit like Albert's mock, NOT dark"). `FigureFrame`'s five
 * modes are all dark-interior recipes (`DARK_FIGURE`/`DARK_COMPOUND`), so
 * this composes its own bordered `<figure>` directly from `paper-tokens.ts`
 * instead — the exact same "flat hairline-ruled paper card, no card-in-card"
 * recipe `PricingSection.tsx`'s tier cards already use in place of the dark
 * `Surface` primitive (that file's own comment: "NO card-in-card... drops
 * Surface entirely for a plain hairline-ruled paper div instead of
 * forking/extending an unowned primitive" — same reasoning applies here:
 * `ui/Surface.tsx` always fills `DARK_SURFACE.base`, so it cannot render a
 * light card without a scope-expanding edit to a shared primitive this lane
 * does not own).
 *
 * ACCESSIBILITY: every row's name + status chip is REAL visible text (not a
 * canvas re-render of information stated elsewhere), so there is no
 * `aria-hidden` motion layer and no `sr-only` text-alternative to author —
 * unlike `HeroJourneyFigure`'s animated canvas, this figure's DOM *is* the
 * content. Only the decorative glyphs (source icons, the arrow, the glow)
 * are `aria-hidden`.
 *
 * REUSE: `BrandIcons.tsx`'s `SourceBrandIcon` (true Meta/TikTok/WhatsApp
 * marks in brand colour, built for exactly this "lead-flow constellation"
 * shape) had zero live consumers before this file — its original caller,
 * `LeadFlowDiagram.tsx`, was deleted in an earlier landing round. This is
 * the first real, wired consumer, not a new icon system.
 *
 * MOTION (contract §H + MOTION LAW): staggered row entrance, a gentle
 * per-row chip pulse "sweep", one quiet arrow pulse, and a quiet glow behind
 * the Cosmos terminus — all native CSS `@keyframes` declared inline
 * (`<style>`, the same zero-client-JS mechanism `ui/Marquee.tsx` already
 * uses; no motion library, no IntersectionObserver — the whole figure is
 * static server-rendered markup that starts animating the instant it
 * paints, i.e. "plays on load, never scroll-driven"). `transform`/`opacity`
 * only, per the contract's hard motion law. `prefers-reduced-motion` is
 * handled ENTIRELY in that same `<style>` block (no JS branch needed): every
 * animation is disabled and every element forced to its resting, fully
 * legible state — the composed end-state the contract requires.
 *
 * `'use client'` is required only because `useLocale()` reads React
 * context (`useContext` cannot run in a Server Component) — there is no
 * other state, effect, or event handler in this file.
 */

import type { ReactNode } from 'react';
import { CalendarClock, FileSpreadsheet, Globe, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import { LANDING_CONTENT } from '../constants/content';
import { SourceBrandIcon } from './BrandIcons';
import { Chip } from '../ui/Chip';
import { CosmosMark } from '../ui/CosmosMark';
import { IllustrativeLabel } from '../ui/IllustrativeLabel';
import {
  PAPER_ACCENT_INK,
  PAPER_ATTENTION_INK,
  PAPER_BAND,
  PAPER_FOCUS,
  PAPER_INK,
  PAPER_RULE,
} from '../ui/paper-tokens';

// One icon per real source id (content.ts `hero.convergence.sources[].id`,
// a plain locale-invariant slot key — same convention `hero.vignette.
// card.id` already uses). Meta/TikTok/WhatsApp get their true brand mark
// (via `SourceBrandIcon`, real distinct platforms); the four generic
// categories get a neutral lucide glyph, mirroring `BrandIcons.tsx`'s own
// documented rule ("'Website' is a category, not a brand -> neutral globe").
const SOURCE_ICON: Record<string, (className: string) => ReactNode> = {
  meta: (className) => <SourceBrandIcon name="Meta" className={className} />,
  tiktok: (className) => <SourceBrandIcon name="TikTok" className={className} />,
  whatsapp: (className) => <SourceBrandIcon name="WhatsApp" className={className} />,
  website: (className) => <Globe className={className} aria-hidden="true" />,
  email: (className) => <Mail className={className} aria-hidden="true" />,
  spreadsheet: (className) => <FileSpreadsheet className={className} aria-hidden="true" />,
  calendar: (className) => <CalendarClock className={className} aria-hidden="true" />,
};

// Scoped, namespaced keyframes — declared once as a module constant (never
// reallocated per render), emitted via a plain `<style>` tag exactly like
// `ui/Marquee.tsx`'s own "CSS-only, 0 client JS" loop. Entrance: 7 rows at a
// tight 45ms cascade (max ~270ms before the last row lands — a quiet beat,
// not a slow reveal). Dot pulse: a longer, gentler per-row stagger so the
// status chips read as a slow "sweep" across the list rather than a
// synchronized blink. Arrow + glow: single continuous quiet pulses.
// `transform`/`opacity` only throughout (MOTION LAW). Every animation is
// fully disabled under `prefers-reduced-motion: reduce`, forcing the
// resting/legible end-state — a CSS-level concern, never a JS branch.
//
// AMBIENT-OPACITY LAW (2026-08-13, visual review: "the first source chip
// renders washed-out pink while its siblings are deep red", desktop AND
// mobile, always chip 1). It was the cycle, not a stray token: the pulse used
// to sit on `.cmg-convergence-chip`, the wrapper around the WHOLE Chip, and
// faded text + border + fill together from opacity 1 to 0.55. Chip 1 carries
// `animation-delay: 0ms`, so it is simply the first to reach the trough
// (t ~= 1.3s, then every 2.6s) — which is exactly when a screenshot taken
// shortly after paint catches it. "Always chip 1" is the delay-0 signature.
// Measured, not eyeballed: `PAPER_ATTENTION_INK` text composited against the
// chip's own fill fell to 2.74:1 at the trough — under even the 3:1 large-text
// floor — and crossed below the AA 4.5:1 text floor at ~0.85 opacity, so MOST
// of the cycle was non-compliant, not just its lowest point. The "washed-out
// pink" the reviewer saw is literally #C27183, the computed composite.
// THE LAW, now: an infinite ambient loop NEVER touches a text-bearing element.
// It modulates only non-text marks, which answer to the 3:1 floor:
//   - the chip's `[data-slot='dot']` (Chip renders it `bg-current`, so it
//     still reads as the same rose) pulses 1 -> 0.65 = 3.35:1 against the chip
//     fill, clear of 3:1 with margin (0.6 measured 3.04:1 — too thin);
//   - the arrow glyph's own resting floor was raised 0.55 -> 0.8, because at
//     0.55 it measured 2.25:1 against the band and only clears 3:1 above
//     ~0.72 — the same defect, one element over, fixed in the same pass
//     rather than left for the next screenshot;
//   - the terminus glow (0.14 -> 0.3) is deliberately EXEMPT: a blurred
//     `aria-hidden` backdrop wash that carries no information, which is what
//     WCAG's own pure-decoration carve-out is for.
// The chip's text and border now hold `PAPER_ATTENTION_INK` at full strength
// at every instant of the cycle. Class-killed by
// `scripts/check-landing-ambient-opacity.mjs`.
const CONVERGENCE_MOTION_CSS = `
@keyframes cmg-convergence-row-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes cmg-convergence-dot-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.65; }
}
@keyframes cmg-convergence-arrow-pulse {
  0%, 100% { opacity: 0.8; transform: translateY(0); }
  50% { opacity: 1; transform: translateY(3px); }
}
@keyframes cmg-convergence-glow-pulse {
  0%, 100% { opacity: 0.14; }
  50% { opacity: 0.3; }
}
.cmg-convergence-row {
  animation: cmg-convergence-row-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
.cmg-convergence-row:nth-child(1) { animation-delay: 0ms; }
.cmg-convergence-row:nth-child(2) { animation-delay: 45ms; }
.cmg-convergence-row:nth-child(3) { animation-delay: 90ms; }
.cmg-convergence-row:nth-child(4) { animation-delay: 135ms; }
.cmg-convergence-row:nth-child(5) { animation-delay: 180ms; }
.cmg-convergence-row:nth-child(6) { animation-delay: 225ms; }
.cmg-convergence-row:nth-child(7) { animation-delay: 270ms; }
.cmg-convergence-chip {
  opacity: 1;
}
.cmg-convergence-chip [data-slot='dot'] {
  opacity: 1;
  animation: cmg-convergence-dot-pulse 2.6s ease-in-out infinite;
}
.cmg-convergence-row:nth-child(1) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 0ms; }
.cmg-convergence-row:nth-child(2) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 220ms; }
.cmg-convergence-row:nth-child(3) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 440ms; }
.cmg-convergence-row:nth-child(4) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 660ms; }
.cmg-convergence-row:nth-child(5) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 880ms; }
.cmg-convergence-row:nth-child(6) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 1100ms; }
.cmg-convergence-row:nth-child(7) .cmg-convergence-chip [data-slot='dot'] { animation-delay: 1320ms; }
.cmg-convergence-arrow {
  opacity: 0.8;
  animation: cmg-convergence-arrow-pulse 2.2s ease-in-out infinite;
}
/* @ambient-opacity-ok reason=blurred aria-hidden backdrop wash behind the terminus; carries no information and has no glyph or text inside it, so WCAG's pure-decoration carve-out applies rather than the 3:1 non-text floor */
.cmg-convergence-glow {
  opacity: 0.14;
  animation: cmg-convergence-glow-pulse 3.2s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .cmg-convergence-row,
  .cmg-convergence-chip,
  .cmg-convergence-chip [data-slot='dot'],
  .cmg-convergence-arrow,
  .cmg-convergence-glow {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
  .cmg-convergence-glow {
    opacity: 0.18 !important;
  }
}
`;

export default function HeroConvergenceFigure() {
  const { t } = useLocale();
  const { convergence } = LANDING_CONTENT.hero;

  return (
    <figure
      data-hero-convergence
      className={cn(
        'relative w-full overflow-hidden rounded-[12px] border p-4 sm:p-5',
        PAPER_RULE.hairline.border,
        PAPER_BAND.alt.bg
      )}
    >
      <style>{CONVERGENCE_MOTION_CSS}</style>

      {/* Header — mono kicker (IllustrativeLabel, paper-safe surface) +
          title + a plain hairline "Seven places" tag (the same className
          override IllustrativeLabel itself uses internally for
          `surface="paper"` — Chip's own `variant="system"` styling is
          dark-only by default). */}
      <div className="relative mb-3 sm:mb-4">
        <IllustrativeLabel
          text={t(convergence.kicker)}
          surface="paper"
          className="static bottom-auto right-auto z-auto mb-2 flex w-fit shrink-0"
        />
        <div className="flex items-center justify-between gap-3">
          <p className={cn('text-[15px] sm:text-base font-semibold', PAPER_INK.primary.text)}>
            {t(convergence.fragmentedTitle)}
          </p>
          <Chip
            variant="system"
            label={t(convergence.fragmentedTag)}
            className={cn(PAPER_RULE.hairline.border, PAPER_INK.muted.text)}
          />
        </div>
      </div>

      {/* Seven fragmented lead sources — icon + real name + real red status
          chip. Rows wrap (never truncate the chip's failure-mode wording)
          so the mobile floor (320px) never overflows horizontally. */}
      <div className={cn('divide-y', PAPER_RULE.hairline.divide)}>
        {convergence.sources.map((source) => (
          <div
            key={source.id}
            data-source-id={source.id}
            className="cmg-convergence-row flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 py-2.5 first:pt-0 last:pb-0"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center">
                {SOURCE_ICON[source.id]?.('size-full') ?? (
                  <Globe className="size-full" aria-hidden="true" />
                )}
              </span>
              <span className={cn('truncate text-[13px] sm:text-sm', PAPER_INK.secondary.text)}>
                {t(source.name)}
              </span>
            </span>
            {/* R8 review FIX 1 (CRIT contrast): `statusTone="attention"`
                keeps the chip's LIGHT TINTED FILL (`TONE.attention.chipFill`,
                a 12% wash — a background, so the text-contrast formula never
                applied to it), but its glyph/border recipe (`#FB7185`) is
                verified for DARK exhibit interiors only and composited to
                2.01:1 here on `PAPER_BAND.alt`. `PAPER_ATTENTION_INK` is the
                registry's contrast-proven paper-surface rose (paper-tokens.ts
                + paper-tokens.test.ts, 5.73:1 worst case); passing it through
                `className` overrides text + border via twMerge exactly the
                way the sibling paper override on the header Chip above does,
                and the `dot` inherits it automatically (Chip renders the dot
                as `bg-current`). `tone.ts`'s dark recipes are untouched. */}
            <span className="cmg-convergence-chip shrink-0">
              <Chip
                variant="status"
                statusTone="attention"
                dot
                label={t(source.chip)}
                className={cn(PAPER_ATTENTION_INK.text, PAPER_ATTENTION_INK.border)}
              />
            </span>
          </div>
        ))}
      </div>

      {/* Amber convergence beat — PAPER_ACCENT_INK is the registry's
          readable-amber-as-glyph token (paper-tokens.ts), the exact
          "icon stroke / status word directly against a paper band" use
          case its own doc comment names; never a hand-typed hex. */}
      <div className="relative flex flex-col items-center gap-1 py-4">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn('cmg-convergence-arrow size-5', PAPER_ACCENT_INK.text)}
        >
          <path d="M12 5v14" />
          <path d="m19 12-7 7-7-7" />
        </svg>
        <span
          className={cn('text-[11px] font-bold uppercase tracking-[0.08em]', PAPER_ACCENT_INK.text)}
        >
          {t(convergence.arrowLabel)}
        </span>
        <span className={cn('text-xs', PAPER_INK.muted.text)}>{t(convergence.arrowSub)}</span>
      </div>

      {/* Terminus — CosmosMark (the real product mark, ui/CosmosMark) + the
          CMG Workflow wordmark, navbar treatment (font-bold tracking-tight,
          LandingNavbar.tsx's own logo recipe). A quiet amber glow sits
          behind it (opacity-animated only, PAPER_FOCUS.hex — the registry's
          brand-amber FILL value — never a hand-typed hex). */}
      <div
        className={cn(
          'relative flex items-center gap-3 overflow-hidden rounded-[10px] border p-3 sm:p-4',
          PAPER_RULE.hairline.border,
          PAPER_BAND.deep.bg
        )}
      >
        <span
          aria-hidden="true"
          data-slot="terminus-glow"
          className="cmg-convergence-glow pointer-events-none absolute -left-4 -top-4 size-16 rounded-full blur-2xl"
          style={{ backgroundColor: PAPER_FOCUS.hex }}
        />
        <span
          className={cn(
            'relative flex size-9 shrink-0 items-center justify-center rounded-[8px] border',
            PAPER_RULE.hairline.border,
            PAPER_BAND.base.bg
          )}
        >
          <CosmosMark size={20} className={PAPER_INK.primary.text} />
        </span>
        <span className="relative min-w-0">
          <span
            className={cn(
              'block truncate text-[15px] font-bold tracking-tight',
              PAPER_INK.primary.text
            )}
          >
            {t(convergence.terminusName)}
          </span>
          <span className={cn('block truncate text-xs', PAPER_INK.muted.text)}>
            {t(convergence.terminusSub)}
          </span>
        </span>
      </div>
    </figure>
  );
}
